import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { forkJoin, timer } from 'rxjs';

import { NotificationsButton } from '../notifications-button/notifications-button';
import { Badge } from '../badge/badge';
import { TopbarPopover } from '../popover/topbar-popover';
import { PopoverPanelDirective } from '../popover/popover-panel.directive';
import { ContextService } from '../../../core/context.service';
import { AlertService } from '../../../modules/alerts/services/alert.service';
import { AlertRuleService } from '../../../modules/alerts/services/alert-rule.service';
import { Alert, AlertRule } from '../../../modules/alerts/models/alert.model';
import { relativeTime } from '../../utils/relative-time';

const POLL_INTERVAL_MS = 60_000;
const FIRING_PREVIEW_SIZE = 5;
const RESOLVED_PREVIEW_SIZE = 3;

/**
 * AlertBell — the top-bar notifications bell backed by the alerts API (alerts
 * design, option 1g). The badge carries the exact FIRING count; the dropdown
 * (a shared `app-topbar-popover`, same shell as the weather chip) shows the
 * newest firing alerts, a "resolved recently" section, and a view-all link to
 * /:org/alerts. Polls once a minute (matching the backend's ≤60 s alert
 * latency) and refetches on open so the panel is never stale.
 *
 * Tenant-portal only — the alert endpoints are tenant-scoped via the JWT, so
 * the admin back-office keeps the plain bell (see StatusToolbar).
 */
@Component({
  selector: 'app-alert-bell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    TranslocoDirective,
    NotificationsButton,
    Badge,
    TopbarPopover,
    PopoverPanelDirective,
  ],
  host: { class: 'inline-flex' },
  template: `
    <ng-container *transloco="let t; read: 'alerts.bell'">
      <app-topbar-popover
        #popover="appTopbarPopover"
        [width]="380"
        [padded]="false"
        [ariaLabel]="t('label')"
        (opened)="refresh()"
      >
        <app-notifications-button [count]="badgeCount()" (activated)="popover.toggle()" />

        <ng-template appPopoverPanel>
          @if (firing().length === 0 && resolved().length === 0) {
            <div class="px-4 py-6 text-center text-[13px] text-ink-3">{{ t('empty') }}</div>
          }

          @if (firing().length > 0) {
            <div
              class="px-4 pb-2 pt-3 text-[12px] font-semibold uppercase tracking-wide text-ink-2"
            >
              {{ t('firing', { count: firingTotal() ?? firing().length }) }}
            </div>
            @for (alert of firing(); track alert.id) {
              <div class="flex gap-3 px-4 py-2.5">
                <app-badge class="mt-0.5" color="red" />
                <div class="min-w-0 flex-1">
                  <div class="truncate text-[14px] font-semibold">
                    {{ alert.subject }} · {{ ruleName(alert) }}
                  </div>
                  <div class="truncate text-[12px] text-ink-2">{{ alert.detail }}</div>
                </div>
                <span class="whitespace-nowrap text-[12px] text-ink-3">{{ ago(alert.startedAt) }}</span>
              </div>
            }
          }

          @if (resolved().length > 0) {
            <div
              class="border-t border-hairline px-4 pb-2 pt-3 text-[12px] font-semibold uppercase tracking-wide text-ink-2"
            >
              {{ t('resolvedRecently') }}
            </div>
            @for (alert of resolved(); track alert.id) {
              <div class="flex gap-3 px-4 py-2.5">
                <app-badge class="mt-0.5" color="green" [check]="true" />
                <div class="min-w-0 flex-1">
                  <div class="truncate text-[14px] font-semibold">
                    {{ alert.subject }} · {{ ruleName(alert) }}
                  </div>
                  <div class="truncate text-[12px] text-ink-2">
                    {{ t('recoveredAfter', { duration: firedFor(alert) }) }}
                  </div>
                </div>
                <span class="whitespace-nowrap text-[12px] text-ink-3">{{
                  ago(alert.resolvedAt)
                }}</span>
              </div>
            }
          }

          @if (organizationName(); as org) {
            <a
              [routerLink]="['/', org, 'alerts']"
              (click)="popover.close()"
              class="block bg-sunken px-4 py-3 text-center text-[13px] font-semibold text-link no-underline hover:underline"
            >
              {{ t('viewAll') }}
            </a>
          }
        </ng-template>
      </app-topbar-popover>
    </ng-container>
  `,
})
export class AlertBell {
  private readonly alertService = inject(AlertService);
  private readonly ruleService = inject(AlertRuleService);
  private readonly contextService = inject(ContextService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly firing = signal<Alert[]>([]);
  protected readonly firingTotal = signal<number | null>(null);
  protected readonly resolved = signal<Alert[]>([]);
  private readonly rules = signal<AlertRule[]>([]);

  private readonly organization = toSignal(this.contextService.organization$, {
    initialValue: null,
  });
  protected readonly organizationName = computed(() => this.organization()?.name ?? null);

  protected readonly badgeCount = computed(() => {
    const count = this.firingTotal();
    return count != null && count > 0 ? count : null;
  });

  private readonly ruleById = computed(() => new Map(this.rules().map((r) => [r.id, r])));

  constructor() {
    // Refresh once a minute, and immediately whenever the organization context
    // (re)arrives — the endpoints are tenant-scoped, so no org means no fetch.
    timer(0, POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refresh());
    this.contextService.organization$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refresh());
  }

  protected ruleName(alert: Alert): string {
    return (
      (alert.ruleId != null ? this.ruleById().get(alert.ruleId)?.name : null) ?? alert.detail
    );
  }

  protected ago(iso: string | null): string {
    return relativeTime(iso);
  }

  /** How long the alert was firing, e.g. "3h 12m". */
  protected firedFor(alert: Alert): string {
    if (!alert.resolvedAt) return relativeTime(alert.startedAt);
    const minutes = Math.max(
      1,
      Math.round(
        (new Date(alert.resolvedAt).getTime() - new Date(alert.startedAt).getTime()) / 60_000,
      ),
    );
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }

  protected refresh(): void {
    if (!this.contextService.getOrganization()) return;
    forkJoin({
      firing: this.alertService.getAlerts('FIRING', 0, FIRING_PREVIEW_SIZE),
      resolved: this.alertService.getAlerts('RESOLVED', 0, RESOLVED_PREVIEW_SIZE),
      rules: this.ruleService.getRules(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ firing, resolved, rules }) => {
          this.firing.set(firing.data);
          this.firingTotal.set(firing.totalElements);
          this.resolved.set(resolved.data);
          this.rules.set(rules.data ?? []);
        },
        // Silent: the bell just goes stale until the next tick.
        error: () => undefined,
      });
  }
}
